import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, RefreshCw, FileMinus, ExternalLink, X, Download, Upload, FileText, Ban, Mail, CheckCircle2, Clock, FileCheck, XCircle, PenSquare } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

export default function CreditNotes() {
  const { active } = useCompany();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState([]);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);
  const [q, setQ] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [timelineFilter, setTimelineFilter] = useState("all");
  const fileInputRef = useRef(null);

  const load = async (nextSkip = 0, overrides = {}) => {
    if (!active) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: String(nextSkip), limit: String(limit) });
      const qv = overrides.q ?? q;
      const fv = overrides.fromDate ?? fromDate;
      const tv = overrides.toDate ?? toDate;
      if (qv) params.set("q", qv);
      if (fv) params.set("from_date", fv);
      if (tv) params.set("to_date", tv);
      const { data } = await api.get(`/credit-notes?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSummary(data.summary || []);
      setSkip(nextSkip);
    } finally { setLoading(false); }
  };

  const filterBySparklinePoint = (point) => {
    // Point carries {start, end, count, total, is_current} — set both date
    // inputs so the user can see the range in the header, then reload.
    setFromDate(point.start);
    setToDate(point.end);
    load(0, { fromDate: point.start, toDate: point.end });
    toast.info(`Filtered to ${point.start} → ${point.end}`);
  };

  useEffect(() => { load(0); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => {
    if (!active) return;
    api.get("/credit-notes/digest/preview?period=week")
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [active, total]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/credit-notes/${id}`);
      setSelected(data);
    } catch (e) { /* ignore */ }
  };

  const submitBulk = async (dryRun) => {
    if (!bulkFile) {
      toast.error("Please choose a CSV file first");
      return;
    }
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", bulkFile);
      const { data } = await api.post(
        `/credit-notes/bulk-refund?dry_run=${dryRun}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setBulkResult(data);
      if (!dryRun && data.ok > 0) {
        toast.success(`Refunded ${data.ok} of ${data.total} rows`);
        load(0);
      } else if (dryRun) {
        toast.info(`Preview: ${data.ok} would refund, ${data.failed} would fail`);
      } else if (data.failed > 0) {
        toast.warning(`${data.failed} rows failed — see the report below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk refund failed");
    } finally { setBulkBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = "invoice_number,refund_amount,reason\nINV-001,100.00,requested_by_customer\nINV-002,,duplicate\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk-refund-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const voidCN = async (cn) => {
    const reason = window.prompt(
      `Void credit note ${cn.number}?\n\n` +
      `This reverses the reversing journal AND un-refunds the invoice. ` +
      `The Stripe refund itself is NOT reversed — if the money must go back ` +
      `to the customer's card, create a new charge separately.\n\n` +
      `Enter a short reason (required):`,
      "operator_correction",
    );
    if (!reason) return;
    try {
      const { data } = await api.post(
        `/credit-notes/${cn.id}/void`,
        { reason },
      );
      toast.success(`Voided ${data.credit_note_number}`);
      setSelected(null);
      load(skip);
    } catch (e) { toast.error(e?.response?.data?.detail || "Void failed"); }
  };

  const sendDigestNow = async () => {
    if (!window.confirm(
      "Email this month's refund digest to the workspace owner right now? " +
      "This is safe to run repeatedly."
    )) return;
    try {
      const { data } = await api.post(`/credit-notes/digest/send-now`);
      if (data.sent) {
        toast.success(`Digest emailed to ${data.recipient} · ${data.count} credit notes`);
      } else {
        toast.info(`Digest not sent: ${(data.reason || "").replace(/_/g, " ")}`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="credit-notes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">Refunds & Reversing Journals</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">Credit Notes</h1>
          <p className="text-sm text-[#64748B]">
            Every refund on a paid invoice creates a signed credit note and a proportional
            reversing double-entry voucher (Sales Return + tax + COGS).
          </p>
        </div>
        <button
          data-testid="credit-notes-refresh"
          onClick={() => load(0)}
          className="btn-ghost border border-[#E2E8F0] w-full sm:w-auto justify-center"
        >
          <RefreshCw size={14}/> Refresh
        </button>
        <button
          data-testid="digest-send-now"
          onClick={sendDigestNow}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-[#B45309] border border-[#B45309] hover:bg-[#FEF3C7] w-full sm:w-auto justify-center"
          title="Email monthly refund digest to workspace owner"
        >
          <Mail size={14}/> Send Digest
        </button>
        <button
          data-testid="bulk-refund-open"
          onClick={() => { setBulkResult(null); setBulkFile(null); setBulkOpen(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#1E3A8A] hover:bg-[#1E40AF] w-full sm:w-auto justify-center"
        >
          <Upload size={14}/> Bulk Refund (CSV)
        </button>
      </div>

      {preview && (
        <div className="rounded-lg border border-[#0F172A] bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] p-5 text-white" data-testid="weekly-digest-preview">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
            <div>
              <div className="text-[10px] tracking-[0.22em] text-[#B45309] font-bold uppercase mb-1">
                Weekly Digest Preview
              </div>
              <div className="text-lg font-semibold">{preview.label}</div>
              <div className="text-xs text-white/60 mt-1 font-mono-data">
                {preview.start} → {preview.end} · running totals
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.22em] text-[#B45309] font-bold uppercase mb-1">
                Credit Notes
              </div>
              <div className="text-4xl font-bold font-mono-data">{preview.grand_count}</div>
              {preview.trend && preview.trend.length > 1 && (() => {
                const vals = preview.trend.map(t => t.count);
                const max = Math.max(1, ...vals);
                const w = 120, h = 34;
                const step = w / (vals.length - 1);
                const points = vals.map((v, i) => `${i * step},${h - (v / max) * (h - 4) - 2}`).join(" ");
                const last = preview.trend[preview.trend.length - 1];
                const prev = preview.trend[preview.trend.length - 2] || last;
                const delta = last.count - prev.count;
                const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "▬";
                const deltaColor = delta > 0 ? "#F59E0B" : delta < 0 ? "#10B981" : "#94A3B8";
                return (
                  <div className="mt-2 flex items-center justify-end gap-2" data-testid="preview-sparkline">
                    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-95">
                      <defs>
                        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F59E0B" stopOpacity="0.35"/>
                          <stop offset="100%" stopColor="#F59E0B" stopOpacity="0"/>
                        </linearGradient>
                      </defs>
                      <polygon
                        points={`0,${h} ${points} ${w},${h}`}
                        fill="url(#sparkFill)"
                      />
                      <polyline
                        points={points}
                        fill="none"
                        stroke="#F59E0B"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      {vals.map((v, i) => {
                        const t = preview.trend[i];
                        const tip = `${t.start} → ${t.end}: ${t.count} CN${t.count !== 1 ? "s" : ""}${t.total ? ` · ${t.total.toLocaleString()}` : ""} · Click to filter`;
                        return (
                          <circle
                            key={i}
                            cx={i * step}
                            cy={h - (v / max) * (h - 4) - 2}
                            r={i === vals.length - 1 ? 3 : 1.8}
                            fill={i === vals.length - 1 ? "#F59E0B" : "#FFFFFF"}
                            stroke={i === vals.length - 1 ? "#0F172A" : "none"}
                            strokeWidth="1"
                            data-testid={`sparkline-pt-${i}`}
                            style={{ cursor: "pointer" }}
                            onClick={(e) => { e.stopPropagation(); filterBySparklinePoint(t); }}
                          >
                            <title>{tip}</title>
                          </circle>
                        );
                      })}
                      {/* Invisible larger hit-targets so tiny dots are still tappable */}
                      {vals.map((v, i) => {
                        const t = preview.trend[i];
                        return (
                          <circle
                            key={`hit-${i}`}
                            cx={i * step}
                            cy={h - (v / max) * (h - 4) - 2}
                            r={9}
                            fill="transparent"
                            style={{ cursor: "pointer" }}
                            data-testid={`sparkline-hit-${i}`}
                            onClick={(e) => { e.stopPropagation(); filterBySparklinePoint(t); }}
                          />
                        );
                      })}
                    </svg>
                    <div className="text-[11px] font-mono-data" style={{ color: deltaColor }}>
                      {arrow} {Math.abs(delta)}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
          {preview.by_currency.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {preview.by_currency.map((c) => (
                <div key={c.currency} className="rounded-md bg-white/8 border border-white/10 p-3" data-testid={`preview-ccy-${c.currency}`}>
                  <div className="text-[10px] tracking-widest text-white/60 uppercase">{c.currency}</div>
                  <div className="text-lg font-bold font-mono-data text-[#F59E0B] mt-1">
                    {fmtMoney(c.total, c.currency)}
                  </div>
                  <div className="text-[11px] text-white/60 mt-0.5">{c.count} note{c.count !== 1 ? "s" : ""}</div>
                </div>
              ))}
            </div>
          )}
          {preview.by_reason.length > 0 && (
            <div className="mt-4 border-t border-white/10 pt-3">
              <div className="text-[10px] tracking-[0.22em] text-[#B45309] font-bold uppercase mb-2">Top Reasons</div>
              <div className="flex flex-wrap gap-2">
                {preview.by_reason.map((r, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/8 text-xs border border-white/10">
                    <span className="text-white/80">{r.reason}</span>
                    <span className="text-[#F59E0B] font-mono-data font-semibold">{r.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {preview.grand_count === 0 && (
            <div className="text-sm text-white/60 mt-2">No refunds this week yet.</div>
          )}
        </div>
      )}

      {summary.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {summary.map((s) => (
            <div key={s.currency} className="card-surface p-4" data-testid={`cn-summary-${s.currency}`}>
              <div className="overline mb-1">{s.currency}</div>
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-xs text-[#64748B]">Total refunded</div>
                  <div className="text-lg font-bold font-mono-data text-[#DC2626]">
                    {fmtMoney(s.total_amount, s.currency)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#64748B]">Notes</div>
                  <div className="text-lg font-bold font-mono-data">{s.count}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-surface p-4">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search size={16} className="text-[#94A3B8]"/>
            <input
              data-testid="credit-notes-search"
              className="input-clean"
              placeholder="Search CN or invoice number…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(0); }}
            />
          </div>
          <input type="date" data-testid="cn-from-date" className="input-clean" style={{ width: 160 }}
            value={fromDate} onChange={(e) => setFromDate(e.target.value)}/>
          <input type="date" data-testid="cn-to-date" className="input-clean" style={{ width: 160 }}
            value={toDate} onChange={(e) => setToDate(e.target.value)}/>
          <button className="btn-ghost border border-[#E2E8F0]" onClick={() => load(0)}>Apply</button>
          {(fromDate || toDate) && (
            <button
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-[#B45309] bg-[#FEF3C7] border border-[#B45309] hover:bg-[#FDE68A]"
              onClick={() => { setFromDate(""); setToDate(""); load(0, { fromDate: "", toDate: "" }); }}
              data-testid="cn-clear-date-filter"
              title="Clear the sparkline / date filter"
            >
              <X size={11}/> Clear filter
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <FileMinus size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B] text-sm">
              {loading ? "Loading…" : "No credit notes yet. They will appear here the moment a refund is issued."}
            </p>
          </div>
        ) : (
          <>
            <table className="table-clean">
              <thead>
                <tr>
                  <th>CN Number</th>
                  <th>Invoice</th>
                  <th>Party</th>
                  <th>Reason</th>
                  <th>Date</th>
                  <th className="text-right">Amount</th>
                  <th>Voucher</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((cn) => (
                  <tr key={cn.id}
                    data-testid={`credit-note-row-${cn.id}`}
                    className="cursor-pointer"
                    onClick={() => openDetail(cn.id)}
                  >
                    <td className="font-mono-data font-medium text-[#DC2626]">
                      {cn.number}
                      {cn.voided && (
                        <span className="ml-2 chip text-[10px] bg-[#F1F5F9] text-[#64748B]" title="Voided">
                          VOIDED
                        </span>
                      )}
                    </td>
                    <td>
                      {cn.invoice_id ? (
                        <Link
                          to={`/app/invoices/${cn.invoice_id}`}
                          className="text-[#1E3A8A] hover:underline font-mono-data text-xs"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {cn.invoice_number || cn.invoice_id}
                          <ExternalLink size={10} className="inline ml-1"/>
                        </Link>
                      ) : "—"}
                    </td>
                    <td>{cn.party_name || "—"}</td>
                    <td className="text-xs text-[#64748B]">{cn.reason || "—"}</td>
                    <td className="font-mono-data text-[#64748B]">{cn.issue_date}</td>
                    <td className="text-right font-mono-data font-semibold text-[#DC2626]">
                      {fmtMoney(cn.amount, cn.currency)}
                    </td>
                    <td>
                      {cn.voucher_id ? (
                        <span className="chip chip-success text-[10px]" title="Reversing voucher posted">
                          Posted
                        </span>
                      ) : (
                        <span className="chip chip-warn text-[10px]" title="Voucher not posted">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <a
                        href={`${API}/credit-notes/${cn.id}/pdf`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Download audit-ready PDF"
                        data-testid={`cn-pdf-${cn.id}`}
                        className="inline-flex items-center justify-center rounded hover:bg-[#F1F5F9] p-1.5"
                      >
                        <Download size={14} className="text-[#1E3A8A]"/>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between mt-4 text-sm text-[#64748B]">
              <div>Showing {skip + 1}–{Math.min(skip + items.length, total)} of {total}</div>
              <div className="flex gap-2">
                <button
                  className="btn-ghost border border-[#E2E8F0]"
                  disabled={skip === 0 || loading}
                  onClick={() => load(Math.max(0, skip - limit))}
                >Prev</button>
                <button
                  className="btn-ghost border border-[#E2E8F0]"
                  disabled={skip + items.length >= total || loading}
                  onClick={() => load(skip + limit)}
                >Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
          data-testid="cn-detail-modal"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">Credit Note</div>
                <h2 className="text-xl font-bold text-[#DC2626] font-mono-data flex items-center gap-2">
                  {selected.number}
                  {selected.voided && (
                    <span className="chip text-[10px] bg-[#F1F5F9] text-[#64748B] line-through" data-testid="cn-voided-badge">
                      VOIDED
                    </span>
                  )}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={`${API}/credit-notes/${selected.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cn-detail-pdf"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A]/5"
                >
                  <Download size={14}/> PDF
                </a>
                {!selected.voided && (
                  <button
                    data-testid="cn-detail-void"
                    onClick={() => voidCN(selected)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border border-[#DC2626] text-[#DC2626] hover:bg-[#FEE2E2]"
                  >
                    <Ban size={14}/> Void
                  </button>
                )}
                <button
                  data-testid="cn-detail-close"
                  onClick={() => setSelected(null)}
                  className="btn-ghost"
                ><X size={18}/></button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm mb-4">
              <div>
                <div className="text-xs text-[#64748B]">Invoice</div>
                <Link to={`/app/invoices/${selected.invoice_id}`} className="font-mono-data text-[#1E3A8A] hover:underline">
                  {selected.invoice_number}
                </Link>
              </div>
              <div>
                <div className="text-xs text-[#64748B]">Party</div>
                <div>{selected.party_name || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-[#64748B]">Refund Amount</div>
                <div className="font-mono-data font-bold text-[#DC2626]">
                  {fmtMoney(selected.amount, selected.currency)}
                </div>
              </div>
              <div>
                <div className="text-xs text-[#64748B]">Issue Date</div>
                <div className="font-mono-data">{selected.issue_date}</div>
              </div>
              <div className="col-span-2">
                <div className="text-xs text-[#64748B]">Reason</div>
                <div>{selected.reason || "—"}</div>
              </div>
              {selected.stripe_refund_id && (
                <div className="col-span-2">
                  <div className="text-xs text-[#64748B]">Stripe Refund ID</div>
                  <div className="font-mono-data text-xs">{selected.stripe_refund_id}</div>
                </div>
              )}
            </div>

            {selected.voucher ? (
              <div className="border-t border-[#E2E8F0] pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="overline">Reversing Journal</div>
                    <div className="font-semibold font-mono-data">
                      {selected.voucher.number}
                    </div>
                  </div>
                  <span className="chip chip-success text-[10px]">
                    {selected.voucher.type}
                  </span>
                </div>
                <table className="table-clean text-sm">
                  <thead>
                    <tr><th>Ledger</th><th className="text-right">Dr</th><th className="text-right">Cr</th></tr>
                  </thead>
                  <tbody>
                    {(selected.voucher.lines || []).map((ln, i) => (
                      <tr key={i}>
                        <td>{ln.ledger_name}</td>
                        <td className="text-right font-mono-data">
                          {ln.dr > 0 ? fmtMoney(ln.dr, selected.currency) : ""}
                        </td>
                        <td className="text-right font-mono-data">
                          {ln.cr > 0 ? fmtMoney(ln.cr, selected.currency) : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="border-t border-[#E2E8F0] pt-4 text-sm text-[#64748B]">
                Reversing voucher not posted yet.
              </div>
            )}

            {selected.timeline && selected.timeline.length > 0 && (
              <div className="border-t border-[#E2E8F0] pt-4 mt-4" data-testid="cn-timeline">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="overline">Approval & Audit Timeline</div>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1 rounded-md border border-[#E2E8F0] p-0.5" data-testid="cn-timeline-filter">
                      {[
                        { key: "all",       lbl: "All" },
                        { key: "approvals", lbl: "Approvals" },
                        { key: "voids",     lbl: "Voids" },
                        { key: "posting",   lbl: "Posting" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          data-testid={`cn-timeline-filter-${opt.key}`}
                          onClick={() => setTimelineFilter(opt.key)}
                          className={`px-2.5 py-1 text-[11px] font-semibold rounded transition ${
                            timelineFilter === opt.key
                              ? "bg-[#0F172A] text-white"
                              : "text-[#64748B] hover:bg-[#F1F5F9]"
                          }`}
                        >
                          {opt.lbl}
                        </button>
                      ))}
                    </div>
                    <a
                      href={(() => {
                        const kindMap = {
                          approvals: "approval_requested,approval_approved,approval_rejected",
                          voids: "voided",
                          posting: "created,voucher_posted,email_sent",
                          all: "",
                        };
                        const kinds = kindMap[timelineFilter] || "";
                        const q = kinds ? `?kinds=${encodeURIComponent(kinds)}` : "";
                        return `${API}/credit-notes/${selected.id}/timeline.csv${q}`;
                      })()}
                      target="_blank"
                      rel="noreferrer"
                      title="Export filtered timeline as CSV"
                      data-testid="cn-timeline-export-csv"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold text-[#B45309] border border-[#B45309] hover:bg-[#FEF3C7]"
                    >
                      <Download size={11}/> CSV
                    </a>
                    <a
                      href={(() => {
                        const kindMap = {
                          approvals: "approval_requested,approval_approved,approval_rejected",
                          voids: "voided",
                          posting: "created,voucher_posted,email_sent",
                          all: "",
                        };
                        const kinds = kindMap[timelineFilter] || "";
                        const q = kinds ? `?kinds=${encodeURIComponent(kinds)}` : "";
                        return `${API}/credit-notes/${selected.id}/timeline.pdf${q}`;
                      })()}
                      target="_blank"
                      rel="noreferrer"
                      title="Print filtered timeline as an audit-ready PDF"
                      data-testid="cn-timeline-export-pdf"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold text-[#1E3A8A] border border-[#1E3A8A] hover:bg-[#EEF2FF]"
                    >
                      <FileText size={11}/> PDF
                    </a>
                  </div>
                </div>
                <ol className="relative border-l-2 border-[#E2E8F0] ml-2 space-y-4">
                  {(() => {
                    const filter = timelineFilter;
                    const match = (ev) => {
                      if (filter === "all") return true;
                      if (filter === "approvals") return ev.kind.startsWith("approval");
                      if (filter === "voids") return ev.kind === "voided";
                      if (filter === "posting") return ev.kind === "voucher_posted" || ev.kind === "email_sent" || ev.kind === "created";
                      return true;
                    };
                    const events = selected.timeline.filter(match);
                    if (events.length === 0) {
                      return (
                        <li className="ml-4 text-sm text-[#94A3B8] italic" data-testid="cn-timeline-empty">
                          No events match this filter.
                        </li>
                      );
                    }
                    return events.map((ev, i) => {
                    const map = {
                      created:              { icon: PenSquare,   color: "#1E3A8A", bg: "#DBEAFE" },
                      approval_requested:   { icon: Clock,       color: "#B45309", bg: "#FEF3C7" },
                      approval_approved:    { icon: CheckCircle2,color: "#16A34A", bg: "#DCFCE7" },
                      approval_rejected:    { icon: XCircle,     color: "#DC2626", bg: "#FEE2E2" },
                      voucher_posted:       { icon: FileCheck,   color: "#0F172A", bg: "#F1F5F9" },
                      email_sent:           { icon: Mail,        color: "#0F172A", bg: "#E0E7FF" },
                      voided:               { icon: Ban,         color: "#DC2626", bg: "#FEE2E2" },
                    };
                    const cfg = map[ev.kind] || map.created;
                    const Icon = cfg.icon;
                    return (
                      <li key={i} className="ml-4" data-testid={`cn-timeline-${ev.kind}`}>
                        <span
                          className="absolute -left-[13px] w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ background: cfg.bg }}
                        >
                          <Icon size={12} style={{ color: cfg.color }}/>
                        </span>
                        <div className="text-sm font-semibold text-[#0F172A]">
                          {ev.label}
                        </div>
                        <div className="text-xs text-[#64748B] font-mono-data mt-0.5">
                          {ev.at ? new Date(ev.at).toLocaleString() : "—"}
                          {ev.actor_email && (
                            <span className="ml-2 text-[#1E3A8A]">· {ev.actor_email}</span>
                          )}
                        </div>
                        {ev.detail && Object.entries(ev.detail).filter(([_k, v]) => v !== null && v !== undefined && v !== "").length > 0 && (
                          <div className="text-xs text-[#64748B] mt-1">
                            {Object.entries(ev.detail).filter(([_k, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                              <span key={k} className="mr-3">
                                <span className="text-[#94A3B8]">{k.replace(/_/g, " ")}:</span>{" "}
                                <span className="font-mono-data text-[#0F172A]">{String(v)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    );
                  });
                  })()}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}

      {bulkOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => !bulkBusy && setBulkOpen(false)}
          data-testid="bulk-refund-modal"
        >
          <div
            className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">Batch Operation</div>
                <h2 className="text-xl font-bold text-[#0F172A]">Bulk Refund via CSV</h2>
                <p className="text-sm text-[#64748B] mt-1">
                  Upload a CSV with <code className="font-mono-data">invoice_number</code>,{" "}
                  <code className="font-mono-data">refund_amount</code>,{" "}
                  <code className="font-mono-data">reason</code>. Leave{" "}
                  <code className="font-mono-data">refund_amount</code> blank to refund the full remaining balance.
                </p>
              </div>
              <button
                data-testid="bulk-refund-close"
                onClick={() => !bulkBusy && setBulkOpen(false)}
                className="btn-ghost"
              ><X size={18}/></button>
            </div>

            <button
              onClick={downloadTemplate}
              data-testid="bulk-refund-template-dl"
              className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A] hover:underline mb-4"
            >
              <FileText size={14}/> Download CSV template
            </button>

            <label
              htmlFor="bulk-csv-input"
              className="block border-2 border-dashed border-[#CBD5E1] rounded-lg p-8 text-center cursor-pointer hover:border-[#1E3A8A] hover:bg-[#F8FAFC]"
              data-testid="bulk-refund-dropzone"
            >
              <Upload size={28} className="mx-auto text-[#94A3B8] mb-2"/>
              <div className="text-sm font-semibold text-[#0F172A]">
                {bulkFile ? bulkFile.name : "Click to choose a CSV file"}
              </div>
              <div className="text-xs text-[#64748B] mt-1">Max 5 MB · UTF-8</div>
              <input
                id="bulk-csv-input"
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
                className="hidden"
                data-testid="bulk-refund-input"
              />
            </label>

            <div className="flex justify-end gap-2 mt-5">
              <button
                data-testid="bulk-refund-dry-run"
                onClick={() => submitBulk(true)}
                disabled={bulkBusy || !bulkFile}
                className="btn-ghost border border-[#E2E8F0] disabled:opacity-50"
              >
                Preview (dry-run)
              </button>
              <button
                data-testid="bulk-refund-submit"
                onClick={() => submitBulk(false)}
                disabled={bulkBusy || !bulkFile}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#B45309] hover:bg-[#92400E] disabled:opacity-50"
              >
                {bulkBusy ? <RefreshCw size={14} className="animate-spin"/> : <Upload size={14}/>}
                {bulkBusy ? "Processing…" : "Process Refunds"}
              </button>
            </div>

            {bulkResult && (
              <div className="mt-5 border-t border-[#E2E8F0] pt-4">
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="text-center p-2 rounded bg-[#F1F5F9]">
                    <div className="text-xs text-[#64748B]">Total</div>
                    <div className="text-lg font-bold font-mono-data">{bulkResult.total}</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[#DCFCE7]">
                    <div className="text-xs text-[#166534]">OK</div>
                    <div className="text-lg font-bold font-mono-data text-[#166534]">{bulkResult.ok}</div>
                  </div>
                  <div className="text-center p-2 rounded bg-[#FEE2E2]">
                    <div className="text-xs text-[#991B1B]">Failed</div>
                    <div className="text-lg font-bold font-mono-data text-[#991B1B]">{bulkResult.failed}</div>
                  </div>
                </div>
                <table className="table-clean text-xs">
                  <thead>
                    <tr><th>Row</th><th>Invoice</th><th>Status</th><th>Detail</th></tr>
                  </thead>
                  <tbody>
                    {bulkResult.rows.map((r) => (
                      <tr key={r.row} data-testid={`bulk-row-${r.row}`}>
                        <td className="font-mono-data">{r.row}</td>
                        <td className="font-mono-data">{r.invoice_number || "—"}</td>
                        <td>
                          {r.status === "ok" && <span className="chip chip-success text-[10px]">Refunded</span>}
                          {r.status === "would_refund" && <span className="chip chip-info text-[10px]">Would refund</span>}
                          {r.status === "failed" && <span className="chip chip-danger text-[10px]">Failed</span>}
                        </td>
                        <td className="text-[11px] text-[#64748B]">
                          {r.credit_note_number ||
                            (r.status === "would_refund" ? `${r.amount} ${r.currency}` : r.error)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
